import React, {
  createContext,
  useContext,
  useCallback,
  useEffect,
  useState,
} from "react";
import { useForm, FormProvider as RHFProvider } from "react-hook-form";
import { joiResolver } from "src/common/joiResolver";
import PropTypes from "prop-types";
import { Space, Form } from "antd";
import { focusFirstError } from "src/common/interaction";
import Joi from "joi";

const FormContext = createContext(null);

export const useFormContext = () => {
  const context = useContext(FormContext);
  if (!context) {
    throw new Error("useFormContext must be used within a FormProvider");
  }
  return context;
};

export const FormProvider = ({
  children,
  onSubmit,
  schema = Joi.object(),
  defaultValues = {},
  url = "",
  layout = "vertical",
  labelCol,
  wrapperCol,
}) => {
  const methods = useForm({
    resolver: joiResolver(schema),
    defaultValues,
    mode: "onChange",
  });
  console.log("defaultValues", defaultValues);

  const { handleSubmit, setError, clearErrors, trigger } = methods;

  // Trigger validation on initialization
  useEffect(() => {
    Object.keys(defaultValues).forEach((key) => {
      defaultValues[key] && trigger(key);
    });
  }, [trigger, defaultValues]);

  const onFinish = async () => {
    if (!Object.keys(methods.formState.errors).length) {
      await handleSubmit(handleFormSubmit)();
    }

    focusFirstError(methods.formState.errors);
  };

  const handleFormSubmit = useCallback(
    async (data) => {
      try {
        await onSubmit(data);
      } catch (error) {
        if (error.response?.data?.errors) {
          Object.entries(error.response.data.errors).forEach(
            ([field, message]) => {
              setError(field, {
                type: "server",
                message: Array.isArray(message) ? message[0] : message,
              });
            }
          );
        }
      }
    },
    [onSubmit, setError]
  );

  const formConfig = {
    layout,
    labelCol,
    wrapperCol,
  };

  const contextValue = {
    ...methods,
    formConfig,
    defaultValues,
    methods,
    onFinish
  };

  return (
    <FormContext.Provider value={contextValue}>
      <RHFProvider {...methods}>
        {children}
      </RHFProvider>
    </FormContext.Provider>
  );
};

FormProvider.propTypes = {
  // children: PropTypes.node.isRequired,
  onSubmit: PropTypes.func.isRequired,
  schema: PropTypes.object,
  defaultValues: PropTypes.object,
  serverError: PropTypes.string,
  direction: PropTypes.oneOf(["vertical", "horizontal"]),
  size: PropTypes.oneOf(["small", "middle", "large"]),
  layout: PropTypes.oneOf(["horizontal", "vertical", "inline"]),
  labelCol: PropTypes.shape({
    span: PropTypes.number,
    offset: PropTypes.number,
  }),
  wrapperCol: PropTypes.shape({
    span: PropTypes.number,
    offset: PropTypes.number,
  }),
  style: PropTypes.object,
};

export default FormProvider;
